import { useState, useEffect } from 'react';
import { useConnect, useDisconnect, useSignMessage } from 'wagmi';
import { requestRoninWalletConnector, ConnectorErrorType } from '@sky-mavis/tanto-connect';

import { getWalletNonce, linkWallet } from '../services/wallet.service';
import { isRoninBrowser, isMetaMaskBrowser } from '../utils/browser';

const LINK_WALLET_MSG =
  'Welcome to Fishing Frenzy!\n\nSign this message to link your wallet\n\nThis request will not trigger a blockchain transaction or cost any gas fees.\n\nNonce: ';

const useLinkWallet = () => {
  const [roninAvailable, setRoninAvailable] = useState(false);
  const [roninConnector, setRoninConnector] = useState(null);

  const { connectAsync, connectors } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();

  // Check Ronin availability
  useEffect(() => {
    requestRoninWalletConnector()
      .then((connector) => {
        setRoninAvailable(true);
        setRoninConnector(connector);
      })
      .catch((err) => {
        if (err.name === ConnectorErrorType.PROVIDER_NOT_FOUND) {
          setRoninAvailable(false);
        } else {
          console.error('[Ronin Wallet] Error requesting connector:', err);
        }
      });
  }, []);

  const getWalletAvailability = () => {
    // Check MetaMask availability
    const metamaskConnector = connectors.find((c) => c.name === 'MetaMask');
    const metaMaskAvailable = !!metamaskConnector;

    // alert( // debug
    //   `getWalletAvailability: ${JSON.stringify({
    //     ronin: roninAvailable,
    //     metamask: metaMaskAvailable,
    //     isRoninBrowser,
    //     isMetaMaskBrowser,
    //   })}`
    // );

    return {
      ronin: roninAvailable,
      metamask: metaMaskAvailable,
      isRoninBrowser,
      isMetaMaskBrowser,
    };
  };

  const linkExternalWallet = async (providerKey) => {
    try {
      // Use Ronin Wallet
      if (providerKey === 'ronin') {
        if (!roninAvailable || !roninConnector) throw new Error('Ronin Wallet is not available');

        const accounts = await roninConnector.requestAccounts();
        const address = accounts[0];
        const { data: nonce } = await getWalletNonce({ address });
        const message = `${LINK_WALLET_MSG}${nonce}`;
        const provider = await roninConnector.getProvider();

        const signature = await provider.request({
          method: 'personal_sign',
          params: [message, address],
        });

        const res = await linkWallet({ message, signature });
        return {
          success: true,
          message: `Ronin wallet ${address.slice(0, 7)}...${address.slice(-4)} linked successfully`,
          data: res.data,
        };
      }

      if (providerKey === 'metamask') {
        const metamaskConnector = connectors.find((c) => c.name === 'MetaMask');
        if (!metamaskConnector) throw new Error('MetaMask connector not found');

        await disconnectAsync(); // reset state
        const { accounts } = await connectAsync({ connector: metamaskConnector });
        const address = accounts[0];

        const { data: nonce } = await getWalletNonce({ address });
        const message = `${LINK_WALLET_MSG}${nonce}`;
        const signature = await signMessageAsync({ message });

        const res = await linkWallet({ message, signature });
        return {
          success: true,
          message: `MetaMask wallet ${address.slice(0, 7)}...${address.slice(-4)} linked successfully`,
          data: res.data,
        };
      }
      throw new Error(`Unsupported wallet provider: ${providerKey}`);
    } catch (err) {
      console.error('[LinkWallet] Error:', err);
      // alert(`Err LinkWallet: ${err.message}`); // debug
      return { success: false, message: err.message };
    }
  };

  return { linkWallet: linkExternalWallet, getWalletAvailability };
};

export default useLinkWallet;
